import type {ChangeEvent} from 'react';

import {useThemeColors} from '@nfq/colors';
import {useFormField, type ValidationFunc} from '@nfq/react-form';

/**
 * Custom hook to manage the state and behavior of a checkbox input field.
 * This hook provides controlled state management for a checkbox component using form field state logic.
 * It initializes the checkbox with a default boolean value and handles changes to the checkbox state.
 * The hook returns a boolean `isActive` to indicate whether the checkbox is currently checked and a `handleChange`
 * function that updates the state on user interaction.
 *
 * @param name          The unique name used to identify the form field.
 * @param initialValue  The checked state of the checkbox.
 * @param keepOnUnmount Whether to retain the field value when the component unmounts.
 * @param validation    Optional validation function to validate the field value.
 * @param onChange      Custom callback function that executes when the field value changes.
 * @returns An object containing the `isActive` state and `handleChange` event handler.
 *
 * @example
 * ```tsx
 * const { isActive, handleChange } = useCheckbox('acceptTerms', false);
 *
 * return (
 *   <input type="checkbox" checked={isActive} onChange={handleChange} />
 * );
 * ```
 */
export const useCheckbox = (
    name: string,
    initialValue: boolean,
    keepOnUnmount: boolean,
    validation: ValidationFunc,
    onChange: (value: boolean) => void

) => {
    const {error, handleFieldChange, value} = useFormField(name, initialValue, initialValue, validation, keepOnUnmount);
    const colors = useThemeColors();

    /**
     * Event handler for handling change events on an input element.
     *
     * @param event The change event.
     */
    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        handleFieldChange(event.target.checked);
        onChange(event.target.checked);
    };

    return {
        colors,
        error,
        handleChange,
        value
    };
};